<template>
  <div style="width: 100%;">
    <v-data-table
      v-if="canSeeTable"
      v-model="selectedUsers"
      :headers="tab === 'web' ? headersWeb : headersMobile"
      :items="fillArrayWithUsers"
      :search="searchFromParent"
      :page.sync="paginationAssociatedUsers.page"
      :items-per-page.sync="paginationAssociatedUsers.rowsPerPage"
      :show-select="selectable"
      @toggle-select-all="selectAll"
      :loading="loading"
      loading-text=""
      :items-per-page-text="rowsPerPageText"
      :items-per-page-items="rowsPerPageItems"
      :header-props="{ sortIcon }"
      :footer-props="{ prevIcon, nextIcon,'items-per-page-options':rowsPerPageItems  }"
      @pagination="setRowsOnLocalStorage(dataTableAssociatedUsersId,paginationAssociatedUsers.rowsPerPage)"
      class="elevation-1"
      :class="{
        'select-all-disabled ': !hasPermissionToEdit,
        'no-show-permission': !selectable
      }"
    >
      <template v-slot:item="props">
        <tr @click="viewItem(props.item)">
          <td v-if="selectable">
            <v-checkbox
              :input-value="
                selectedUsers.find(item => item.uuid === props.item.uuid)
              "
              @change="toggleCheckbox(props.item)"
              color="accent"
              hide-details
              :ripple="false"
              :disabled="!hasPermissionToEdit"
              @click.stop
            />
          </td>

          <td class="data-table-name">
            <v-layout row align-center>
              <img :src="props.item.image" class="avatar data-table-logo" />

              <span class="data-table-name data-table-td-content">
                {{ getName(props.item) }}
              </span>
            </v-layout>
          </td>

          <td>{{ props.item.username }}</td>

          <td>{{ props.item.email }}</td>

          <td>
            <v-layout v-if="props.item.status" row align-center>
              <div class="company-status-icon mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("Active") }}
              </span>
            </v-layout>

            <v-layout v-else row align-center>
              <div class="company-status-icon status-inactive mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("Inactive") }}
              </span>
            </v-layout>
          </td>
        </tr>
      </template>

      <template v-slot:pageText="props">
        {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
        {{ props.itemsLength }}
      </template>

      <template v-slot:no-results>
        <data-table-no-results :search="searchFromParent" />
      </template>

      <template v-slot:no-data>
        <div v-if="loading" />
        <v-alert v-else class="data-table-no-data" icon="warning">
          {{ $t(userType === "web" ? "NoDataWeb" : "NoDataMobile") }}
        </v-alert>
      </template>
    </v-data-table>

    <div v-if="!canSeeTable" :class="{ 'page-section': !selectable }">
      <app-no-permission-warning />
    </div>
  </div>
</template>

<script>
import { userListsMixin } from "@/mixins/management/userListsMixin";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import LockSVG from "@/assets/management/lock.svg";
import AppNoPermissionWarning from "@/components/_Molecules/AppNoPermissionWarning";

export default {
  name: "AssociatedUsers",

  components: { AppNoPermissionWarning, DataTableNoResults, LockSVG },

  mixins: [managementDatatableMixin, userListsMixin],

  props: {
    /**
     * Type of action desired when opening the form.
     * @example "edit", "view", "create"
     */
    actionType: {
      type: String,
      required: true
    },

    /**
     * Determine user type
     * @example "web" , "mobile"
     */
    userType: {
      type: String,
      required: true
    },

    /**
     * Determine which parent page is using
     * @example "company", "workspace", "client_role"
     */
    from: {
      type: String
    },

    /**
     * List of associated users
     */
    associatedUsers: {
      Type: Array
    },

    /**
     * Determines whether the user has permission to edit permissions
     */
    hasPermissionToEdit: {
      Type: Boolean
    }
  },

  data() {
    return {
      /**
       * List of users
       */
      users: [],
      /**
       * List of selected users
       */
      dataTableAssociatedUsersId: "dataTableAssociatedUsersId" ,
      paginationAssociatedUsers: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableAssociatedUsersId")
      },
      selectedUsers: []
    };
  },

  computed: {
    /**
     * Determines if the user can edit or Remove the items.
     * @return {boolean}
     */
    userCanEditOrRemoveItems() {
      return false;
    },

    /**
     * Determines if the user can list the items.
     * @return {boolean}
     */
    canSeeTable() {
      return (
        this.userHasManagementPermission("user_list") &&
        this.userHasManagementPermission(
          this.associationPermissionName + "_list"
        )
      );
    },

    /**
     * Determines the name of the permission dependent on the value of the 'from' variable
     * @returns {string}
     */
    associationPermissionName() {
      if (this.from === "company") {
        return "user_company";
      } else if (this.from === "workspace") {
        return "workspace_user";
      } else if (this.from === "role") {
        return "client_role";
      }
    }
    ,
    fillArrayWithUsers(){
      if(this.selectable){
        if(this.userType==="web"){
          return this.usersWeb;
        }else if(this.userType==="mobile"){
          return this.usersMobile;
        }
      }else{
        return this.users;
      }
    }
  },

  watch: {
    /**
     * Whenever the variable selectedUsers is changed,
     * it emits an event with the new value of the variable.
     * @param newValue
     */
    selectedUsers(newValue) {
      this.$emit("associate-users", newValue);
    }
  },

  mounted() {
    this.loading = true;
    this.selectedUsers = this.associatedUsers;
    if (this.canSeeTable) {
      if (this.selectable) {
        this.getAllUsers();
      } else {
        this.getUsersByType();
      }
    } else {
      this.loading = false;
      console.log("The user doesn't have the necessary permissions");
    }
  },

  methods: {
    /**
     * Get the list of user by type
     */
    getUsersByType() {
      for (let user of this.associatedUsers) {
        this.pushUser(user);
      }
      this.loading = false;
    },

    /**
     * Add user in users list
     * @param user
     */
    pushUser(user) {
      user.name = user.first_name + " " + user.last_name;
      user.name = user.name.replace("null", "");
      if (user.type === this.userType) {
        this.users.push(user);
      }
    },

    /**
     * Select or deselect all visible items on the table page
     * @param items
     */
    selectAll(items) {
      if (items.value) {
        this.selectedUsers = items;
      } else {
        this.selectedUsers = [];
      }
    },

    /**
     * Select or deselect the item you receive
     * @param item
     */
    toggleCheckbox(item) {
      let idx = this.selectedUsers.findIndex(user => user.id === item.id);
      if (idx === -1) {
        this.selectedUsers.push(item);
      } else this.selectedUsers.splice(idx, 1);
    },

    /**
     * Push to the page to see the selected user
     * @param {Object} item - Selected user
     */
    viewItem(item) {
      if(this.actionType === 'view'){
        if (this.userHasManagementPermission("user_show")) {
          /**
           * @property {String} name - Name of the route
           * @property {Object} params - Parameters needed for the route path
           */
          this.$router.push({ name: "user", params: { id: item.id } });
        } else {
          this.$toasted.clear();
          this.$toasted.global.toast_error(this.$i18n.t("MissingPermissions"));
        }
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/AssociatedUsers.vue